import { LoadingBlock, Table } from '@orobix/orobix-fe-components'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useQueryClient } from 'react-query'
import ReactTooltip from 'react-tooltip'

import { OptimizationProcess, PipelineType } from '../../../types'
import { createSocket } from '../../../utils/webSocket'
import { useStyles } from './OptimizationProcessesList.styles'
import { PipelineTypeToggle } from './PipelineTypeToggle'
import { useColumns } from './useColumns'
import { useOptimizationProcesses } from './useOptimizationProcesses'
import { useQueryParamFiltering } from './useQueryParamFiltering'

type Columns = OptimizationProcess & {
  actions?: string
}

export const OptimizationProcessesList = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const queryClient = useQueryClient()

  const { filtering, sorting, handleSort, handleFilter } =
    useQueryParamFiltering<keyof Columns>()

  // The two starting points expose different columns, so the list always
  // shows one type at a time (server-side filter), defaulting to from_empty.
  const pipelineType: PipelineType =
    filtering.pipelineType === 'from_last_config'
      ? 'from_last_config'
      : 'from_empty'

  const { data, isEmpty, hasNextPage, fetchNextPage } =
    useOptimizationProcesses({
      sorting,
      filtering: { ...filtering, pipelineType },
    })

  const columns = useColumns({
    pipelineType,
    sorting,
    filtering,
    handleSort,
    handleFilter,
  })

  useEffect(() => {
    const websocket = createSocket('/optimization-process/status')

    websocket.onmessage = () => {
      queryClient.invalidateQueries(['optimization-processes'])
    }

    return () => {
      websocket.close()
    }
  }, [queryClient])

  return (
    <InfiniteScroll
      loadMore={() => fetchNextPage()}
      hasMore={hasNextPage}
      loader={<LoadingBlock message={t('Loading')} key="loading" />}
    >
      <div className={classes.toolbar}>
        <PipelineTypeToggle
          value={pipelineType}
          onChange={(value) => handleFilter('pipelineType', value)}
        />
      </div>
      <div className={classes.tableWrapper}>
        <Table
          columns={columns}
          data={data}
          alternateRows
          stickyHeader
          isEmpty={isEmpty}
          emptyMessage={t(
            `There's nothing here. Try creating a new {{ itemName }}.`,
            { itemName: t('optimization process') }
          )}
        />
      </div>
      <ReactTooltip
        id="info-tooltip"
        place="top"
        effect="solid"
        className={classes.infoTooltip}
      />
    </InfiniteScroll>
  )
}
